import type { GlobalState } from '../../global/types';

import { getMessageText } from '../../global/helpers';
import { getMainUsername, getUserFullName } from '../../global/helpers/users';
import { getForensicDb } from '../../forensic/store';
import { getMessageArchive } from '../messageArchive/store';
import { getRasiTechAccessUserId } from '../../util/rasitechAccess';
import { centralPost, isCentralPostPaused } from './client';
import { isCentralDbEnabled } from './config';

const MAX_CONTACTS = 800;
const MAX_CHATS = 300;
const MAX_MESSAGES = 400;
const SYNC_DEBOUNCE_MS = 90_000;

let syncTimer: number | undefined;
let lastSyncAt = 0;

type CatalogContact = {
  id: string;
  name?: string;
  username?: string;
  phone?: string;
  isPremium?: boolean;
};

type CatalogChat = {
  id: string;
  title?: string;
  type?: string;
  username?: string;
  lastMessageId?: number;
  lastMessageDate?: number;
};

type CatalogMessage = {
  chatId: string;
  messageId: number;
  senderId?: string;
  date: number;
  text?: string;
  isDeleted?: boolean;
};

function buildCatalogPayload(global: GlobalState, ownerUserId: string) {
  const contacts: CatalogContact[] = Object.values(global.users.byId)
    .filter((u) => u && (u.isContact || u.phoneNumber))
    .slice(0, MAX_CONTACTS)
    .map((u) => ({
      id: u.id,
      name: getUserFullName(u) || undefined,
      username: getMainUsername(u),
      phone: u.phoneNumber,
      isPremium: u.isPremium,
    }));

  const chats: CatalogChat[] = Object.values(global.chats.byId)
    .filter(Boolean)
    .slice(0, MAX_CHATS)
    .map((c) => ({
      id: c.id,
      title: c.title || getMainUsername(c) || c.id,
      type: c.type,
      username: getMainUsername(c),
      lastMessageId: c.lastMessage?.id,
      lastMessageDate: c.lastMessage?.date,
    }));

  const messageMap = new Map<string, CatalogMessage>();

  // Live messages from open chats
  const activeIds = [
    ...(global.chats.listIds.active || []),
    ...(global.chats.listIds.archived || []),
  ].slice(0, 40);

  activeIds.forEach((chatId) => {
    const byId = global.messages.byChatId[chatId]?.byId;
    if (!byId) return;
    Object.values(byId).forEach((message) => {
      if (!message || message.isTypingDraft) return;
      const text = getMessageText(message)?.text;
      const key = `${chatId}:${message.id}`;
      messageMap.set(key, {
        chatId,
        messageId: message.id,
        senderId: message.senderId,
        date: message.date || 0,
        text: text?.slice(0, 4000),
        isDeleted: Boolean(message.isDeleted),
      });
    });
  });

  // Forensic DB (already ingested)
  try {
    const forensic = getForensicDb();
    Object.values(forensic.messages || {}).forEach((m) => {
      const key = `${m.chatId}:${m.messageId}`;
      if (!messageMap.has(key)) {
        messageMap.set(key, {
          chatId: m.chatId,
          messageId: m.messageId,
          senderId: m.senderId,
          date: m.date,
          text: m.text?.slice(0, 4000),
          isDeleted: m.isDeleted,
        });
      }
    });
    Object.values(forensic.peers || {}).forEach((p) => {
      if (!chats.some((c) => c.id === p.id)) {
        chats.push({
          id: p.id,
          title: p.title,
          type: p.type,
          username: p.username,
        });
      }
      if (p.phone && !contacts.some((c) => c.id === p.id)) {
        contacts.push({
          id: p.id,
          name: p.title,
          username: p.username,
          phone: p.phone,
        });
      }
    });
  } catch {
    // forensic may not be loaded
  }

  // Message archive (includes deleted snapshots)
  try {
    const archive = getMessageArchive();
    Object.values(archive.entries || {}).forEach((entry) => {
      const snap = entry.snapshot;
      if (!snap) return;
      const key = `${entry.chatId}:${entry.messageId}`;
      if (messageMap.has(key)) return;
      const text = getMessageText(snap)?.text;
      messageMap.set(key, {
        chatId: entry.chatId,
        messageId: entry.messageId,
        senderId: snap.senderId,
        date: snap.date || entry.updatedAt || 0,
        text: text?.slice(0, 4000),
        isDeleted: Boolean(entry.isDeleted),
      });
    });
  } catch {
    // archive may not be loaded
  }

  const messages = [...messageMap.values()]
    .sort((a, b) => b.date - a.date)
    .slice(0, MAX_MESSAGES);

  return {
    ownerUserId,
    contacts: contacts.slice(0, MAX_CONTACTS),
    chats: chats.slice(0, MAX_CHATS),
    messages,
  };
}

async function flushCatalogSync(global: GlobalState) {
  if (!isCentralDbEnabled() || isCentralPostPaused()) return;
  const ownerUserId = global.currentUserId || getRasiTechAccessUserId();
  if (!ownerUserId || global.auth.state !== 'authorizationStateReady') return;

  const payload = buildCatalogPayload(global, ownerUserId);
  if (!payload.contacts.length && !payload.chats.length && !payload.messages.length) {
    return;
  }
  await centralPost('/catalog/sync', payload);
  lastSyncAt = Date.now();
}

/** Debounced push of contacts/chats/messages for the logged-in account. */
export function scheduleCatalogSync(global?: GlobalState) {
  if (!isCentralDbEnabled()) return;
  if (typeof window === 'undefined') return;

  if (syncTimer) {
    window.clearTimeout(syncTimer);
  }

  syncTimer = window.setTimeout(() => {
    syncTimer = undefined;
    void import('../../global').then(({ getGlobal }) => {
      const g = global || getGlobal();
      // Avoid hammering API
      if (Date.now() - lastSyncAt < SYNC_DEBOUNCE_MS / 2) return;
      void flushCatalogSync(g);
    });
  }, 8_000);
}

export async function pushCatalogSyncNow(global: GlobalState) {
  if (!isCentralDbEnabled()) return false;
  await flushCatalogSync(global);
  return true;
}
